import React, { createContext, useContext, useMemo, useState, useCallback } from 'react';
import { useUser } from '../components/UserContext';
import { useClients, useConvenios } from './DataContext';

// The client portal's "workspace" switcher. A cliente account can hold access
// to several worlds at once — one or more fiados (clientIds) and one or more
// convenios (convenioAccess) — and stacking them in one nav overlaps two
// mental models. Every portal surface below the sidebar selector assumes ONE
// active context: a single fiado (empresa) or a single convenio. There is
// deliberately no aggregated "todo" view, and proyecto-scoped convenio access
// stays inside its convenio context (the proyecto gating lives in the convenio
// pages, not here).
//
// The selection persists per uid in localStorage so a reload keeps the view.

const PortalViewContext = createContext({
  contexts: [],
  activeContext: null,
  activeClienteId: null,
  activeConvenioId: null,
  setActiveContextKey: () => {},
});

export const PortalViewProvider = ({ children }) => {
  const { user, userData } = useUser();
  const { clientsList } = useClients();
  const { conveniosById } = useConvenios();

  const isClient = userData?.rol === 'client';

  const contexts = useMemo(() => {
    if (!isClient || !userData) return [];
    const list = [];
    (userData.clientIds || []).forEach((cid) => {
      const cliente = clientsList.find((c) => c.id === cid);
      list.push({
        key: `cliente:${cid}`,
        type: 'cliente',
        id: cid,
        label: cliente?.nombreEmpresa || 'Empresa',
      });
    });
    Object.keys(userData.convenioAccess || {}).forEach((convenioId) => {
      list.push({
        key: `convenio:${convenioId}`,
        type: 'convenio',
        id: convenioId,
        label: conveniosById[convenioId]?.nombre || 'Convenio',
      });
    });
    return list;
  }, [isClient, userData, clientsList, conveniosById]);

  const storageKey = user?.uid ? `portalView:${user.uid}` : null;
  const [storedKey, setStoredKey] = useState(() => {
    try {
      return storageKey ? localStorage.getItem(storageKey) : null;
    } catch {
      return null;
    }
  });

  // A stored key that no longer matches the user's access falls back to the
  // first context instead of stranding the portal on nothing.
  const activeContext = useMemo(
    () => contexts.find((c) => c.key === storedKey) || contexts[0] || null,
    [contexts, storedKey]
  );

  const setActiveContextKey = useCallback(
    (key) => {
      setStoredKey(key);
      try {
        if (storageKey) localStorage.setItem(storageKey, key);
      } catch {
        // Storage unavailable (private mode) — the in-memory state still works.
      }
    },
    [storageKey]
  );

  const value = useMemo(
    () => ({
      contexts,
      activeContext,
      activeClienteId: activeContext?.type === 'cliente' ? activeContext.id : null,
      activeConvenioId: activeContext?.type === 'convenio' ? activeContext.id : null,
      setActiveContextKey,
    }),
    [contexts, activeContext, setActiveContextKey]
  );

  return <PortalViewContext.Provider value={value}>{children}</PortalViewContext.Provider>;
};

export const usePortalView = () => useContext(PortalViewContext);

export default PortalViewContext;
